import { APP_INITIALIZER, forwardRef, Injectable, LOCALE_ID, NgModule, Provider } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { RouteReuseStrategy } from "@angular/router";

import { IonicModule, IonicRouteStrategy } from "@ionic/angular";

import { DatePipe, registerLocaleData } from "@angular/common";
import {
  HTTP_INTERCEPTORS,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from "@angular/common/http";
import localeDe from "@angular/common/locales/de";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { LoadingBarHttpClientModule } from "@ngx-loading-bar/http-client";
import { AuthModule, LogLevel, OidcConfigService, OidcSecurityService } from "angular-auth-oidc-client";
import { NgcCookieConsentConfig, NgcCookieConsentModule } from "ngx-cookieconsent";
import { NgxImageCompressService } from "ngx-image-compress";
import { QuillModule } from "ngx-quill";
//import registerQuillLanguageTool from "../quill-languagetool";
import { Observable } from "rxjs";

//registerQuillLanguageTool(Quill);
import { tap } from "rxjs/operators";
import { environment } from "../environments/environment";
import { ApiConfiguration } from "./api/api-configuration";
import { ApiModule } from "./api/api.module";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { PositionModalPage } from "./kalkulation-datenbank/position-modal/position-modal.page";
import { SuchePageModule } from "./kalkulation-datenbank/suche/suche.module";
import { LoveItToastService } from "./util/services/love-it-toast.service";

registerLocaleData(localeDe);

export function configureAuth(oidcConfigService: OidcConfigService) {
  return () =>
    oidcConfigService.withConfig({
      stsServer: environment.identityServer,
      redirectUrl: environment.redirectUrl,
      postLogoutRedirectUri: window.location.origin,
      clientId: environment.clientId,
      scope: "openid profile phone permission introspect",
      responseType: "code",
      silentRenew: false,
      logLevel: LogLevel.Debug,
      maxIdTokenIatOffsetAllowedInSeconds: 30000,
      storage: localStorage,
    });
}

const port = window.location.port ? window.location.port : "443";
const rootUrl = window.location.hostname + ":" + port;

export function initApiConfiguration(config: ApiConfiguration): Function {
  return () => {
    config.rootUrl = window.location.protocol + "//" + rootUrl + "/api";
  };
}

export const INIT_API_CONFIGURATION: Provider = {
  provide: APP_INITIALIZER,
  useFactory: initApiConfiguration,
  deps: [ApiConfiguration],
  multi: true,
};

@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  iso8601 = /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(\.\d+)?(([+-]\d\d:\d\d)|Z)?$/;

  constructor(
    private loveItToastService: LoveItToastService,
    private oidcSecurityService: OidcSecurityService,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      tap(
        (event) => {
          if (event instanceof HttpResponse) {
            const body = event.body;
            this.convertToDate(body);
          }
        },
        (err) => {
          if (!err.ok) {
            if (err.status === 401 || err?.error?.error?.includes("401 Client Error:")) {
              this.oidcSecurityService.logoff();
              return;
            }
            const originalStatusText = err.statusText;
            err.statusText = this.getPotentialOverrideErrorText(err.status, err.message, originalStatusText);
            const modifiedMessage = err.message.replace(originalStatusText, err.statusText);

            if (err.status === 424) {
              // this error code is used when the backend intercepts an error
              const errorMessage = `'${err.url}' liefert den Fehler:<br>${err.error.error}`;
              this.loveItToastService.showToastMessage(errorMessage, err.status);
            } else {
              this.loveItToastService.showToastMessage(modifiedMessage, err.status);
            }
          }
        },
      ),
    );
  }

  getPotentialOverrideErrorText(status: number, message: string, originalText: string): string {
    let overrideText: string = null;
    switch (status) {
      case 413:
        overrideText = "Objekt zu groß";
        break;
      case 403:
        overrideText = "Keine Berechtigung";
        break;
      default:
        break;
    }

    switch (message) {
      default:
        break;
    }
    return overrideText ?? originalText;
  }

  convertToDate(body) {
    if (body === null || body === undefined) {
      return body;
    }

    if (typeof body !== "object") {
      return body;
    }

    for (const key of Object.keys(body)) {
      const value = body[key];
      if (this.isIso8601(value)) {
        body[key] = new Date(value);
      } else if (typeof value === "object") {
        this.convertToDate(value);
      }
    }
  }

  isIso8601(value) {
    if (value === null || value === undefined) {
      return false;
    }

    return this.iso8601.test(value);
  }
}

export const API_INTERCEPTOR_PROVIDER: Provider = {
  provide: HTTP_INTERCEPTORS,
  useExisting: forwardRef(() => ApiInterceptor),
  multi: true,
};

const cookieConfig: NgcCookieConsentConfig = {
  cookie: {
    // tslint:disable-next-line:max-line-length
    domain: window.location.hostname, // or 'your.domain.com' // it is mandatory to set a domain, for cookies to work properly (see https://goo.gl/S2Hy2A)
  },
  position: "bottom",
  theme: "classic",
  palette: {
    popup: {
      background: "#004899",
      text: "#ffffff",
      link: "#ffffff",
    },
    button: {
      background: "#ff980e",
      text: "#ffffff",
      border: "transparent",
    },
  },
  type: "opt-out",
  content: {
    message: "Wir verwenden Cookies, um unsere Website laufend an die Anforderungen unserer Nutzer anzupassen",
    allow: "Cookies akzeptieren",
    dismiss: "Cookies ablehnen",
    deny: "Cookies ablehnen",
    link: "",
    href: "",
    policy: "Cookies",
  },
  revokeBtn: "<div></div>",
};

@NgModule({
  declarations: [AppComponent, PositionModalPage],
  imports: [
    BrowserModule,
    IonicModule.forRoot({ innerHTMLTemplatesEnabled: true }),
    AppRoutingModule,
    ApiModule,
    ReactiveFormsModule,
    FormsModule,
    LoadingBarHttpClientModule,
    AuthModule.forRoot(),
    SuchePageModule,
    NgcCookieConsentModule.forRoot(cookieConfig),
    QuillModule.forRoot(),
  ],
  providers: [
    NgxImageCompressService,
    OidcConfigService,
    {
      provide: APP_INITIALIZER,
      useFactory: configureAuth,
      deps: [OidcConfigService],
      multi: true,
    },
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    INIT_API_CONFIGURATION,
    ApiInterceptor,
    API_INTERCEPTOR_PROVIDER,
    { provide: LOCALE_ID, useValue: "de" },
    DatePipe,
    provideAnimationsAsync(),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
